import React, { useRef, useState, useEffect } from 'react';
import { Row, Button, Stack, Icon, Card } from "_core/components/common/UI";
import * as Constants from "_core/configs/Constants";
import { FormatFileSize } from "_core/utils/common";
import './Uploader.scss';

export const Uploader = (props) => {
	const fileInputRef = useRef(null);
	const [isError, setIsError] = useState({status:false, text:""});
	const [limit, setLimit] = useState(1);

	// 1ファイルあたりの最大サイズ(個別指定が無い場合は全アップロード共通の上限を使う)
	const maxSize = props.maxSize ? props.maxSize : Constants.MAX_UPLOAD_FILE_SIZE;
	const maxSizeLabel = FormatFileSize(maxSize);

	const imageTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp'];
	const videoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
	const fileTypeDic = {
		"aac" 	        : "filetype-aac",
		"css" 	        : "filetype-css",
		"csv" 	        : "filetype-csv",
		"md" 	        : "filetype-md",
		"doc" 	        : "filetype-doc",
		"docx" 	    	: "filetype-docx",
		"html" 	    	: "filetype-html",
		"json" 	    	: "filetype-json",
		"mp3" 	        : "filetype-mp3",
		"otf" 	        : "filetype-otf",
		"pdf" 	        : "filetype-pdf",
		"ppt" 	        : "filetype-ppt",
		"pptx" 	    	: "filetype-pptx",
		"psd" 	        : "filetype-psd",
		"raw" 	        : "filetype-raw",
		"sql" 	        : "filetype-sql",
		"svg" 	        : "filetype-svg",
		"tiff" 	    	: "filetype-tiff",
		"tsx" 	        : "filetype-tsx",
		"ttf" 	        : "filetype-ttf",
		"txt" 	        : "filetype-txt",
		"wav" 	        : "filetype-wav",
		"xls" 	        : "filetype-xls",
		"xlsx" 	    	: "filetype-xlsx"
	}

	useEffect(() => {
		if (! props.limit){
			setLimit(1)
			return
		}
		setLimit(props.limit)
	}, [props.limit]);

	// ファイルに変更があったときのハンドラ
	const handleFileChange = (files) => {
		setIsError({status:false, text:""})
		if (props.onFileChanged) {
			props.onFileChanged(files);
		}
	}

	// ファイルを追加する関数
	const addFiles = (addFiles) => {

		// ファイルタイプ判定
		if (! isValidType(addFiles))
		{
			setIsError({status:true, text:"アップロードできない種類のファイルが選択されました。"})
			return
		}

		// ファイルサイズ判定
		if (! isValidSize(addFiles))
		{
			setIsError({status:true, text:`アップロードできるファイルは1つあたり${maxSizeLabel}までです。`})
			return
		}

		const updatedFiles = addFiles.map(file => ({
			file,
			previewUrl: isPreviewable(file) ? URL.createObjectURL(file) : null
		}));

		// ファイル入れ替えタイプの時は、渡されたファイルで保持内容を入れ替え
		if (props.isSwap)
		{
			if (updatedFiles.length > limit)
			{
				setIsError({status:true, text:`アップロードできるファイルは${limit}個までです。`})
				return;
			}
			
			// ファイル変更を通知
			handleFileChange(updatedFiles);
			return
		}

		if ((props.files.length + updatedFiles.length) > limit)
		{
			setIsError({status:true, text:`アップロードできるファイルは${limit}個までです。`})
			return;
		}

		// ファイル変更を通知
		handleFileChange([...props.files, ...updatedFiles]);
	};

	// ファイルを削除する関数
	const removeFile = (index) => {
		// ファイル変更を通知
		handleFileChange(props.files.filter((_, i) => i !== index));
	};

	// ファイル選択ダイアログを開く
	const openFileDialog = () => {
		// 非活性時は開かない
		if (props.disabled) {
			return;
		}
		fileInputRef.current.click(); // ファイル選択ダイアログを開く
	};

	// ドラッグされたときのイベント
	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		// 非活性時はドロップを受け付けないことがカーソルで分かるようにする
		e.dataTransfer.dropEffect = props.disabled ? 'none' : 'copy';
	};

	// ドロップされたときのイベント
	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();

		// 非活性時はドロップ領域がdivのためボタンのdisabledでは止まらない。ここで弾く
		if (props.disabled) {
			return;
		}

		const droppedFiles = Array.from(e.dataTransfer.files);
		addFiles(droppedFiles);
	};

	const handleFileInputChange = (e) => {
		const selectedFiles = Array.from(e.target.files);
		
		// ファイル入力をリセット（同じファイルを再選択できるようにする）
		e.target.value = '';
		
		addFiles(selectedFiles);
	};

	// 画像や動画かどうかを判定する関数
	const isPreviewable = (file) => {
		return imageTypes.includes(file.type) || videoTypes.includes(file.type);
	};

	const isValidType = (files) => {
		// 受付ファイル種別制限がない場合は、全てtrue
		if (!props.accept)
		{
			return true;
		}

		let isValid = true;
		files.forEach((file) => {
			const extension = file.name.split('.').pop().toLowerCase();
			if (! props.accept.includes("." + extension))
			{
				isValid = false;
			}
		});

		return isValid;
	}

	const isValidSize = (files) => {
		return ! files.some((file) => file.size > maxSize);
	}

	// ファイルリストを描画する関数
	const renderFilePreview = (fileWrapper, index) => {
		const { file, previewUrl } = fileWrapper;
		const isImage = imageTypes.includes(file.type);
		const isVideo = videoTypes.includes(file.type);
		const extension = file.name.split('.').pop().toLowerCase();

		return (
			<div key={index} className="file-item">
				<div className="file-preview-wrapper">
					<div className="file-preview">
						{isImage && <img src={previewUrl} alt={file.name} className="thumbnail-image" />}
						{isVideo && (
							<video src={previewUrl} className="thumbnail-video" muted playsInline></video>
						)}
						{
							(!isImage && !isVideo) &&
							<Stack className="thumbnail-file flex-center align-center">
								{
								(extension in fileTypeDic) ?
									<Icon icon={fileTypeDic[extension]} size={32} className="copy-icon"/>
									:
									<Icon icon="file-earmark-text" size={32} className="copy-icon"/>
								}
							</Stack>
						}
						<div className="file-info">
							<div className="file-name">{file.name}</div>
							<div className="file-size">{FormatFileSize(file.size)}</div>
						</div>
					</div>
					<Button className={"remove-button primary"} onClick={() => {removeFile(index)}} disabled={props.disabled}>
						<Stack className="width-full height-full flex-center align-center">
							<Icon icon="x" size={16} className="copy-icon offset-top-1px offset-left-2px"/>
						</Stack>
					</Button>
				</div>
			</div>
		);
	};

	// エラー内容を描画する関数
	const renderError = () => {
		if (! isError.status)
		{
			return null;
		}

		return (
			<Card className="uploader-error info-base width-full padding-16px">
				<Row className="width-full spacing-16px">
					<Stack className="flex-center align-center">
						<Icon icon="info-circle-fill" size={20} className="copy-icon"/>
					</Stack>
					<Stack  className="flex-left align-center">
						{isError.text}
					</Stack>
				</Row>
			</Card>
		);
	};

	if (props.minimum === true)
	{
		return (
			<div className={"uploader minimum " + (props.disabled ? "disabled " : "") + (props.className || '')}>
				{/* ▼　下記は不可視のインプット */}
				<input
					type="file"
					ref={fileInputRef}
					accept={props.accept ? props.accept.join(", ") : '*'}
					style={{ display: 'none' }} // ボタンで代用するため非表示
					onChange={handleFileInputChange}
					disabled={props.disabled}
				/>

				<div
					className="drop-area"
					onDragOver={handleDragOver}
					onDrop={handleDrop}
				>
					<Stack className="flex-center spacing-16px">
						<Row className="width-full flex-center align-center height-fit">
							<Button className={(props.buttonLabel ? ("width-fit " + props.buttonClass) : "upload-button hidden-border width-fit")} onClick={openFileDialog} disabled={props.disabled}>
								{
									props.buttonLabel ? props.buttonLabel :
									<Row>
										<Icon icon="plus" size={20} className="copy-icon"/>
										<div className='text-size-normal text-normal-weight'>ファイルを選択</div>
									</Row>
								}
							</Button>
						</Row>
						{renderError()}
					</Stack>
				</div>
			</div>
		);
	}

	return (
		<div className={"uploader " + (props.disabled ? "disabled " : "") + (props.className || '')}>
			{/* ▼　下記は不可視のインプット */}
			<input
				type="file"
				ref={fileInputRef}
				accept={props.accept ? props.accept.join(", ") : '*'}
				style={{ display: 'none' }} // ボタンで代用するため非表示
				onChange={handleFileInputChange}
				disabled={props.disabled}
			/>

			<div 
				className="drop-area"
				onDragOver={handleDragOver} 
				onDrop={handleDrop} 
			>
				<Stack className="flex-center spacing-16px">
					{
						props.icon ? props.icon :
						<Icon icon="file-earmark-text" size={48} className="copy-icon"/>
					}
					{
						props.label ? props.label :
						<div>ドラッグ&ドロップでファイルを追加</div>
					}
					<div className="text-size-small text-color-weak">最大サイズ: {maxSizeLabel}</div>
					{
						(props.files && props.files.length > 0) &&
						<Row className="file-list spacing-16px flex-center">
							{props.files.map((fileWrapper, index) => renderFilePreview(fileWrapper, index))}
						</Row>
					}
					<Row className="width-full flex-center align-center height-fit">
						<Button className={(props.buttonClass ? ("upload-button width-fit " + props.buttonClass) : "upload-button hidden-border width-fit")} onClick={openFileDialog} disabled={props.disabled}>
							{
								props.buttonLabel ? props.buttonLabel :
								<Row>
									<Icon icon="plus" size={20} className="copy-icon"/>
									<div className='text-size-normal text-normal-weight'>ファイルを選択</div>
								</Row>
							}
						</Button>
					</Row>
					{renderError()}
				</Stack>
			</div>
		</div>
	);
};

export default Uploader;